import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Sparkles, Calendar, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function Meetings() {
  const [meetings, setMeetings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const [m, l] = await Promise.all([api.get("/meetings"), api.get("/leads")]);
      setMeetings(m.data);
      setLeads(l.data);
      if (m.data.length > 0 && !selected) setSelected(m.data[0]);
    } catch {
      toast.error("Failed to load meetings");
    }
  };

  useEffect(() => { load(); }, []);

  const lead = selected ? leads.find((x) => x.lead_id === selected.lead_id) : null;

  const generateBrief = async () => {
    if (!selected) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/meetings/${selected.meeting_id}/brief`);
      setSelected(data);
      setMeetings((prev) => prev.map((m) => (m.meeting_id === data.meeting_id ? data : m)));
      toast.success("Brief generated");
    } catch {
      toast.error("Brief generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const updateStatus = async (status) => {
    try {
      await api.patch(`/meetings/${selected.meeting_id}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const sendReminder = async (kind) => {
    if (!selected) return;
    try {
      const { data } = await api.post(`/meeting-reminders/send-now/${selected.meeting_id}`, { kind });
      if (data.ok) toast.success(`Reminder sent (${kind}) · SMS: ${data.sms?.ok ? "✓" : "—"} Email: ${data.email?.ok ? "✓" : "—"}`);
      else toast.error(data.error || "Reminder failed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reminder failed");
    }
  };

  return (
    <Layout title="Meetings" subtitle="Discovery calls">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List */}
        <div className="lg:col-span-1 bg-card border border-border" style={{ borderRadius: 2 }}>
          <div className="px-4 py-3 border-b border-border overline">Upcoming · Past</div>
          <div className="max-h-[70vh] overflow-y-auto">
            {meetings.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">No meetings yet. Book one from a lead.</div>
            )}
            {meetings.map((m) => {
              const l = leads.find((x) => x.lead_id === m.lead_id);
              const date = new Date(m.scheduled_at);
              return (
                <button
                  key={m.meeting_id}
                  onClick={() => setSelected(m)}
                  data-testid={`meeting-${m.meeting_id}`}
                  className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-secondary/40 transition-colors ${selected?.meeting_id === m.meeting_id ? "bg-secondary/60 accent-left" : ""}`}
                >
                  <div className="font-medium text-sm">{l?.full_name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{l?.company || ""} · {l?.industry || ""}</div>
                  <div className="text-xs font-mono mt-1">{date.toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{m.status}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Brief */}
        <div className="lg:col-span-2 bg-card border border-border" style={{ borderRadius: 2 }}>
          {!selected && <div className="px-6 py-12 text-center text-sm text-muted-foreground">Select a meeting to view the AI pre-meeting brief.</div>}
          {selected && (
            <>
              <div className="px-6 py-5 border-b border-border flex items-start justify-between gap-4">
                <div>
                  <div className="overline mb-1">Pre-Meeting Brief</div>
                  <div className="font-serif text-3xl tracking-tight" style={{ fontWeight: 400 }}>{lead?.full_name}</div>
                  <div className="text-sm text-muted-foreground">{lead?.title} · {lead?.company} · {lead?.industry}</div>
                  <div className="text-xs font-mono mt-2"><Calendar className="h-3 w-3 inline mr-1" strokeWidth={1.5} />{new Date(selected.scheduled_at).toLocaleString("en-AU", { dateStyle: "full", timeStyle: "short" })} · {selected.duration_minutes}m · {selected.location}</div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Button onClick={generateBrief} disabled={generating} size="sm" data-testid="generate-brief" style={{ borderRadius: 2 }}>
                    <Sparkles className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> {generating ? "Generating…" : "Regenerate brief"}
                  </Button>
                  {selected.status === "scheduled" && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => updateStatus("completed")} data-testid="mark-completed" style={{ borderRadius: 2 }}><Check className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Completed</Button>
                      <Button size="sm" variant="outline" onClick={() => updateStatus("no_show")} data-testid="mark-noshow" style={{ borderRadius: 2 }}><X className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> No show</Button>
                    </div>
                  )}
                  <div className="flex flex-col gap-1 mt-1 items-end" data-testid="reminder-row">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">No-show defender</div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => sendReminder("24h")} data-testid="send-reminder-24h" className="text-[11px]">
                        {selected.reminder_24h_sent_at ? "✓ 24h sent" : "Send T-24h"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => sendReminder("2h")} data-testid="send-reminder-2h" className="text-[11px]">
                        {selected.reminder_2h_sent_at ? "✓ 2h sent" : "Send T-2h"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <BriefSection label="Company summary" body={selected.brief?.company_summary} />
                <BriefSection label="Industry analysis" body={selected.brief?.industry_analysis} />
                <BriefList label="Likely spend categories" items={selected.brief?.likely_spend_categories} />
                <BriefList label="Pain points" items={selected.brief?.pain_points} />
                <BriefList label="Trigger events" items={selected.brief?.trigger_events} />
                <BriefList label="Discovery questions" items={selected.brief?.discovery_questions} accent />
                <BriefList label="Likely objections + counters" items={selected.brief?.objections} />
                <BriefList label="Talking points" items={selected.brief?.talking_points} />
                {!selected.brief?.discovery_questions?.length && (
                  <div className="text-sm text-muted-foreground">No brief generated yet — click <strong className="text-foreground">Regenerate brief</strong>.</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

function BriefSection({ label, body }) {
  if (!body) return null;
  return (
    <div>
      <div className="overline mb-1.5">{label}</div>
      <div className="text-sm">{body}</div>
    </div>
  );
}
function BriefList({ label, items, accent }) {
  if (!items || items.length === 0) return null;
  return (
    <div className={accent ? "border-l-2 border-primary pl-4" : ""}>
      <div className="overline mb-2">{label}</div>
      <ul className="space-y-1.5">
        {items.map((it, i) => <li key={i} className="text-sm">— {it}</li>)}
      </ul>
    </div>
  );
}
